import React from 'react';
import { GetStaticProps, GetStaticPropsContext } from 'next';
import useIntersectionObserver from '@react-hook/intersection-observer';

import { LatestVideosDocument, Teacher, TeacherDocument, Video } from '@edx/graphql/generated';
import { useThemeStateContext } from '@edx/contexts/theme';
import { getStaticApolloClient } from '@edx/helpers/apollo';
import { ResponsiveImage } from '@edx/types/responsive-image';
import { useMonthlyFormattedPrice } from '@edx/hooks/use-monthly-formatted-price';
import { Hero } from '@edx/components/hero';
import { SignupBanner } from '@edx/components/banners';
import { BenefitsPanel } from '@edx/components/benefits-panel';
import { LandingProfile } from '@edx/components/landing/components/profile/landing-profile';
import { TestimonialProps } from '@edx/components/testimonials-carousel';
import { ExampleLessons } from '@edx/components/landing/components';
import { VimeoVideo } from '@edx/components/video-tabbed-list';
import { HowItWorks } from '@edx/components/landing/components/how-it-works';
import { VideoSlider } from '@edx/components/video-slider';

type Props = {
  bgImage: ResponsiveImage;
  projectName: string;
  projectShortName: string;
  latestVideos: Video[];
  teacher: Teacher;
  testimonials: TestimonialProps[];
  exampleLessons: VimeoVideo[];
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  const { apolloClient } = await getStaticApolloClient(context);

  const latestVideosQuery = await apolloClient.query({
    query: LatestVideosDocument,
    variables: { filter: { limit: 8 } },
  });

  const { data } = await apolloClient.query({ query: TeacherDocument, variables: { slug: 'miki-santamaria' } });

  // @TODO: THIS IS HARDCODED!!!!
  const testimonials = [
    {
      quote:
        'Miki Santamaria es no sólo un bajista magnífico pero es poseedor del don de saber transmitir lo que conoce y les prometo que van a disfrutar mucho aprendiendo de él.',
      name: 'Abraham Laboriel',
      title: 'Bajista de sesión de Michael Jackson, Ray Charles, Lee Ritenour, etc.',
      avatar: '/img/landings/profile/testimonials/abraham_laboriel_testimonial.jpg',
    },
    {
      quote: 'Miki is a phenomenal player! I’m always excited to be in the studio with him.',
      name: 'Phredley Brown',
      title: 'Guitarrista y director musical de Bruno Mars.',
      avatar: '/img/landings/profile/testimonials/phredly_brown_testimonial.jpg',
    },
  ];

  return {
    props: {
      latestVideos: latestVideosQuery.data.latestVideos,
      teacher: data.teacher,
      testimonials,
    },
  };
};

const profile = {
  name: 'Miki Santamaria',
  imageUrl: '/img/landings/empieza-ahora/empieza_ahora-profile.jpg',
};

export const WelcomePage: React.FC<Props> = ({
  bgImage,
  latestVideos,
  projectName,
  projectShortName,
  teacher,
  testimonials,
  exampleLessons,
}) => {
  const monthlyFormattedPrice = useMonthlyFormattedPrice();
  const [theme, setTheme] = useThemeStateContext();
  const benefitsPanelRef = React.useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver(benefitsPanelRef, { initialIsIntersecting: true });

  React.useEffect(() => {
    if (!benefitsPanelRef.current || !theme?.navBar?.position) return;

    if (isIntersecting && theme.navBar.position !== 'absolute') {
      setTheme({ navBar: { ...theme.navBar, position: 'absolute', variant: 'transparent' } });
    } else if (!isIntersecting && theme.navBar.position !== 'fixed') {
      setTheme({ navBar: { ...theme.navBar, position: 'fixed', variant: 'bordered' } });
    }
  }, [benefitsPanelRef, isIntersecting, theme?.navBar, setTheme]);

  return (
    <>
      <Hero
        bgImage={bgImage}
        heading="Aprende a tocar el bajo con un método moderno y directo."
        preheading={`· ${projectName} ·`}
        primaryButton={{ text: 'Empieza ahora', link: '/precio?nav=f' }}
        ref={benefitsPanelRef}
        variant="large"
      />

      <BenefitsPanel />

      <ExampleLessons lessons={exampleLessons} />

      <SignupBanner
        className="-mt-20"
        header={monthlyFormattedPrice ? `Aprende a tocar el bajo desde sólo ${monthlyFormattedPrice} al mes` : '\u00A0'}
        primaryButton={{
          text: 'Regístrate ahora',
          link: '/precio?nav=f',
        }}
        secondaryButton={{
          text: `Regala ${projectShortName}`,
          link: `/regala-${projectShortName.toLowerCase()}`,
        }}
      />

      <HowItWorks />

      <LandingProfile
        name={teacher.name}
        imageUrl={profile.imageUrl}
        reproductions="60M+"
        subscribers="230K+"
        testimonials={testimonials}
        bio={teacher.bodyHTML}
        link={teacher.slug === 'miki-santamaria' ? '/miki-santamaria' : teacher.resourcePath}
        socialNetworks={{
          facebook: { link: teacher.facebookUrl as string, count: '67K' },
          instagram: { link: teacher.instagramUrl as string, count: '29K' },
          youtube: { link: teacher.youtubeUrl as string, count: '150K' },
        }}
      />

      {latestVideos.length > 0 && (
        <section className="my-10 w-full max-w-6xl mx-auto px-4 md:px-8 py-1 md:py-4">
          <h2 className="font-body uppercase tracking-widest text-gray-300 font-light">Últimos videos</h2>

          <VideoSlider items={latestVideos} />
        </section>
      )}
    </>
  );
};
